export const fr = {
  title: `En savoir plus sur l'épilepsie`,
  category1: {
    title: `Comment fonctionne mon cerveau?`,
    p1: `Le cerveau est un organe très complexe. Il peut être comparé à un grand circuit électrique qui envoie des signaux dans plusieurs parties du corps. Par exemple, lorsque nous touchons à un chaudron trop chaud, notre cerveau commande à notre main de se retirer immédiatement pour éviter les brûlures! Les différentes parties du cerveau sont associées à plusieurs fonctions précises du corps comme le montre le schéma ci-dessous : `,
    p2: `**Les fonctions exécutives sont des capacités qui nous permettent de nous adapter à une situation. Elles incluent notamment la capacité de planifier et d’organiser, la mémoire de travail et la gestion des émotions.`
  },
  category2: {
    title: 'Que se passe-t-il lors d’une crise d’épilepsie?',
    p1: `L’épilepsie peut être définie comme une tendance durable à faire des crises récurrentes de différent type en fonction de la zone du cerveau touchée, déclenchées de manière souvent spontanée.  Lors d’une telle crise, le fonctionnement normal du cerveau est perturbé par un courant électrique excessif.`
  },
  category3: {
    title: 'Quels sont les éléments déclencheurs et les causes de l’épilepsie?',
    p1: `Au titre des déclencheurs et des causes de l’épilepsie, deux notions doivent être distinguées :`,
    bullets: [
      `Les causes de l’épilepsie ou les éléments qui font qu’une personne est atteinte d’épilepsie;`,
      `Les déclencheurs d’une crise d’épilepsie, qui ne sont par ailleurs pas la cause de la maladie elle-même.`
    ],
    causes: {
      title: `Les causes de l'épilepsie`,
      p1: `Le fait qu’une personne soit atteinte d’épilepsie peut trouver sa source dans une prédisposition génétique, bien qu’aucun gène précis n’ait été identifié à cet égard et qu’il n’y pas nécessairement de transmission héréditaire identifiée. Dans ce cas, on parle d’épilepsie en l’absence de lésion cérébrale identifiée.`,
      p2: `Une personne peut être atteinte d’épilepsie comme conséquence d’une atteinte cérébrale par suite d’une malformation congénitale, des séquelles de problèmes à la naissance, un traumatisme crânien, un AVC, une tumeur, une infection du système nerveux central, des maladies génétiques évolutives…`
    },
    triggers: {
      title: `Les déclencheurs de crise`,
      p1: `Il est possible qu’une personne atteinte d’épilepsie ne puisse pas identifier précisément les éléments qui déclenchent ses crises d’épilepsie.`,
      p2: `Cependant, les déclencheurs communs de crise sont les suivants et les personnes atteintes d’épilepsie doivent les connaître pour ajuster leurs habitudes de vie afin de les éviter :`,
      bullets: [
        `L’oubli de prendre les anticonvulsivants;`,
        `Le manque de sommeil;`,
        `Un niveau de fatigue important;`,
        `Un horaire de sommeil irrégulier;`,
        `Un niveau de stress important;`,
        `L’abus d’alcool et de drogue;`,
        `Le sevrage de l’alcool et de drogue;`,
        `La fièvre;`,
        `Les menstruations et les changements hormonaux;`,
        `Le fait de prendre d’autres médicaments qui pourraient interférer avec les anticonvulsivants (comme des antibiotiques, ou des produits naturels);`,
        `Le fait de sauter des repas;`,
        `Le fait de ne pas faire suffisamment d’exercice;`,
        `Pour certaines formes d’épilepsie généralisés: Les lumières vives et clignotantes : jeux vidéo, lumières stroboscopiques, reflets du soleil dans l’eau…`
      ],
      p3: {
        textBefore: `Afin de mieux contrôler les crises, il est recommandé d’utiliser le `,
        link: `journal des crises`,
        textAfter: `.`
      }
    }
  },
  category4: {
    title: `Personnalités célèbres atteintes d’épilepsie`,
    p1: `Lorsque l’épilepsie est traitée adéquatement, il est tout à fait possible de vivre, d’étudier et de travailler normalement. En effet, l’épilepsie n’est pas associée à une baisse des capacités intellectuelles. Les personnes atteintes d’épilepsie pourront entreprendre des carrières similaires à celles qui n’ont pas de diagnostic d’épilepsie.`,
    p2: `D’ailleurs, de nombreuses personnalités connues sont ou étaient atteintes d’épilepsie, ce qui ne les a pas empêchés de laisser leur marque dans le monde, d’exceller dans leurs domaines respectifs et inspirer les générations suivantes.`,
    p3: `Parmi les personnalités célèbres atteintes d’épilepsie, on peut citer:`,
    bullets: [
      `Molière: Grand dramaturge français du XVIIe`,
      `Franklin D. Roosevelt: 32e président des États-Unis`,
      `Alfred Nobel: Inventeur des Prix Nobel`,
      `Lil Nas: Rappeur américain et chanteur-compositeur`,
      `Agatha Christie: Romancière britannique connue pour ces romans policiers`,
      `Edgar Allan Poe: Écrivain américain`
    ]
  },
  category5: {
    title: `Comment diagnostique-t-on l’épilepsie?`,
    p1: `Une personne atteinte de l’épilepsie présente au moins un des critères suivants :`,
    bullets: [
      `Avoir eu au moins 2 crises épileptiques spontanées (non-provoquées) à plus de 24h d’intervalle.`,
      `Avoir eu une première crise épileptique spontanée et être à grand risque d’en refaire une autre (probabilité de plus de 60% de la survenue d’une autre crise sur une période de 10 ans).`,
      `Avoir reçu un diagnostic de syndrome épileptique.`
    ],
    p2: `Comme aucun test ne permet de l’établir systématiquement avec certitude, le diagnostic d’épilepsie peut parfois être difficile à poser. Pour ce faire, les médecins se basent souvent avant tout sur l’histoire et la description des symptômes survenant durant les crises ainsi que sur un examen clinique du patient. Certains examens supplémentaires sont souvent prescrits. Parmi eux, on retrouve fréquemment l’EEG (ou électroencéphalogramme) qui permet d’enregistrer l’activité du cerveau, l’IRM (un type d’imagerie médicale qui permet de bien voir le cerveau), ou encore des prises de sang ou des analyses d'urine. Ces tests permettent de confirmer ou de renforcer la suspicion de diagnostic d’épilepsie et d’éliminer d’autres conditions qui pourraient lui ressembler. Dans certains cas où le diagnostic reste incertain, le médecin peut également proposer au patient une hospitalisation de quelques jours durant laquelle il sera monitoré par EEG et surveillance vidéo. Durant ce séjour à l'hôpital, on tente d’enregistrer simultanément les symptômes du patients et l’activité électrique dans son cerveau pendant une crise. La combinaison de ces informations permet aux neurologues de différencier une crise épileptique d’une crise non épileptique, et les aide à déterminer la région du cerveau impliquée dans l'initiation des crises pour déterminer le meilleur traitement pour le patient.`
  },
  category6: {
    title: `Est-ce qu’il existe différents types d’épilepsie?`,
    p1: `Oui! Les symptômes ressentis lors de la crise diffèrent selon la région du cerveau affectée. Les symptômes changent donc d’une personne à l’autre. On classe l’épilepsie en deux catégories (crises focales et crises généralisées). Pour en savoir plus, cliquez sur la catégorie qui vous intéresse : `,
    ss1: {
      title: `Crise focale`,
      bullets: [
        `Aussi appelée “Aura”;`,
        `Comme son nom l’indique, la crise d'épilepsie dite focale touche seulement une région du cerveau appelée “région focale”. Elle peut parfois s’étendre à d’autres régions du cerveau et devenir une crise d’épilepsie généralisée (voir section crise généralisée);`,
        `De courte durée (1 à 2 minutes);`,
        `Il n’y a généralement pas de perte de conscience, sauf si elle se transforme en crise généralisée. La personne peut avoir de la difficulté à vous répondre lorsque vous lui parlez;`,
        `Apparition de sensations inhabituelles (problèmes de vision ou d’audition, picotements, odeurs étranges, etc.);`,
        `Souvent associées ou suivies d’une fixité du regard;`,
        `Il peut y avoir des mouvements involontaires ou des mouvements automatiques des bras ou des jambes;`,
        `Présentation variable selon la région du cerveau impliquée.`
      ]
    },
    ss2: {
      title: `Crise généralisée`,
      p1: `Les crises d’épilepsie généralisées touchent toutes les zones du cerveau. Elles se regroupent en quatre catégories distinctes : les absences, les crises tonico-cloniques, les crises atoniques et les crises myocloniques.`,
      sss1: {
        bullets: [
          `Elles se développent habituellement durant l’enfance, mais elle est plus rare chez les très jeunes enfants. Elles disparaissent dans 75 % des cas à l’âge adulte;`,
          `Elles sont de courte durée, autour de 10 secondes;`,
          `Elles peuvent arriver plusieurs fois par jour;`,
          {
            text: `Symptômes :`,
            bullets: [
              `La personne tombe soudainement dans la lune (perte de conscience), mais ne tombe pas au sol : elle reste dans la même position qu’avant le début de la crise (assis, debout, etc.);`,
              `La personne ne répond pas lorsqu’on lui parle;`,
              `Il peut y avoir un clignement des paupières et révulsions des yeux;`,
              `Accompagnée de très peu de mouvements des muscles;`
            ]
          },
          `ATTENTION : Elles sont souvent confondues à tort avec un trouble de l’inattention (TDA).`,
          {
            text: `L’absence se distingue de la crise focale avec altération de la conscience :`,
            bullets: [
              `Une crise focale avec altération de la conscience est une crise qui démarre dans une partie spécifique du cerveau puis qui s’étend pour atteindre l’entièreté du cerveau, ce qui cause une perte de conscience. Dans les premiers moments de ce type de crise, la personne peut avoir une aura (une sensation étrange comme une impression de déjà-vu, des picotements, un trouble de vision ou d’audition...).`,
              `Pour sa part, une absence est une crise généralisée d’emblée, c’est-à-dire que le cerveau entier est atteint dès le début. Il n’y a pas d’aura dans ce type de crise et la personne perd conscience dès le début de la crise.`
            ]
          }
        ]
      },
      sss2: {
        bullets: [
          `Ce type de crise comporte deux phases comme l’indique son nom, l’une tonique et l’autre clonique;`,
          {
            text: `Durant la phase tonique, les muscles deviennent soudainement raides et ils se contractent (souvent les muscles du haut du corps et du bas du corps). La personne perd aussi conscience.`,
            bullets: [
              `Cette phase dure de 10 à 30 secondes;`,
              `Chute;`,
              `La personne peut lâcher des cris sans s’en rendre compte;`,
              `Respiration difficile;`
            ]
          },
          {
            text: `Durant la phase clonique, le corps bouge rapidement :`,
            bullets: [
              `Possibilité d’accumulation de salive et/ou de se mordre la langue;`,
              `Possibilité de perte d’urine ou de selles;`,
              `Durée approximative de 1 à 3 minutes;`
            ]
          },
          `À la fin de la crise, la personne reprend lentement conscience en 5 à 30 minutes. Pendant cette période, la personne peut se sentir confuse, agitée, irritable, avoir oublié ce qui s’est passé, avoir mal aux muscles, à la tête et avoir des nausées ou vomissements. On nomme cette période l’état post-ictal.`
        ]
      },
      sss3: {
        bullets: [
          `Il y a une perte de conscience;`,
          `La personne perd soudainement son tonus musculaire et peut donc se retrouver au sol;`,
          `Apparition durant l’enfance en général.`
        ]
      },
      sss4: {
        bullets: [
          `Il n’y a pas de perte de conscience;`,
          `Secousses musculaires brusques et intenses d’une partie ou de l’ensemble du corps pouvant entraîner une chute;`,
          `Elle peut survenir seule ou en série (plusieurs crises myocloniques se suivent).`
        ]
      }
    }
  }
};
